
import React from "react";

// Images from src/ui-assets/help/how-to-sign-up/
import signupImage from "../../ui-assets/help/how-to-sign-up/img1.png";
import verificationImage from "../../ui-assets/help/how-to-sign-up/img2.png";
import dashboardImage from "../../ui-assets/help/how-to-sign-up/img3.png";
import profileImage from "../../ui-assets/help/how-to-sign-up/img4.png";

const steps = [
    {
        number: "01",
        label: "GET STARTED",
        title: "Open EventFork",
        description:
            "Visit EventFork and explore a world of conferences, meetups, workshops, and exciting experiences.",
        image: signupImage,
        alt: "EventFork home page",
        icon: "fa-globe",
        tip: "Start from the official EventFork home page.",
    },
    {
        number: "02",
        label: "CREATE ACCOUNT",
        title: "Enter your details",
        description:
            "Click Log In in the navigation bar, choose Sign Up, and enter your name, email address, and password.",
        image: verificationImage,
        alt: "EventFork account creation interface",
        icon: "fa-user-plus",
        tip: "Use a valid email address that you can access.",
    },
    {
        number: "03",
        label: "VERIFY EMAIL",
        title: "Verify your email address",
        description:
            "Open the verification email sent to your inbox and click Verify email address to activate your account.",
        image: dashboardImage,
        alt: "EventFork email verification process",
        icon: "fa-envelope-circle-check",
        tip: "Check your spam or junk folder if needed.",
    },
    {
        number: "04",
        label: "YOU'RE ALL SET",
        title: "Explore your dashboard",
        description:
            "After successful verification and sign-in, access your account, discover events, and start your EventFork journey.",
        image: profileImage,
        alt: "EventFork account dashboard",
        icon: "fa-compass",
        tip: "Keep your account details up to date.",
    },
];

const styles = `
/* ================================
   EVENTFORK SIGN-UP HELP PAGE
================================ */

.signup-guide,
.signup-guide * {
  box-sizing: border-box;
}

.signup-guide {
  --sg-orange: #ff572d;
  --sg-orange-dark: #e74620;
  --sg-ink: #171b2c;
  --sg-muted: #6e7890;
  --sg-border: #e9eaf0;
  --sg-surface: #ffffff;
  --sg-bg: #f7f7f1ff;

  width: 100%;
  min-height: 100vh;
  color: var(--sg-ink);
  background: var(--sg-bg);
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  overflow: hidden;
}

.signup-guide a {
  color: inherit;
  text-decoration: none;
}

.signup-guide button,
.signup-guide a {
  -webkit-tap-highlight-color: transparent;
}

.signup-guide .sg-container {
  width: min(1120px, calc(100% - 48px));
  margin: 0 auto;
}

/* HERO */

.signup-guide .sg-hero {
  position: relative;
  isolation: isolate;
  padding: 76px 0 88px;
  background:
    radial-gradient(ellipse at 88% 12%, rgba(255, 111, 74, .15), transparent 35%),
    radial-gradient(ellipse at 8% 95%, rgba(99, 102, 241, .09), transparent 35%),
    #ffffff;
  border-bottom: 1px solid #ececf2;
}

.signup-guide .sg-hero::before {
  content: "";
  position: absolute;
  top: -140px;
  right: -110px;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  border: 1px solid rgba(255, 87, 45, .10);
  z-index: -1;
}

.signup-guide .sg-hero::after {
  content: "";
  position: absolute;
  right: 5%;
  bottom: 30px;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: rgba(255, 87, 45, .035);
  filter: blur(5px);
  z-index: -1;
}

.signup-guide .sg-hero-grid {
  display: grid;
  grid-template-columns: 1.05fr .95fr;
  align-items: center;
  gap: 56px;
}

.signup-guide .sg-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 9px 14px;
  margin-bottom: 23px;
  border: 1px solid #ffded4;
  border-radius: 30px;
  background: #fff7f4;
  color: var(--sg-orange-dark);
  font-size: 12px;
  font-weight: 750;
  letter-spacing: 1.1px;
  text-transform: uppercase;
}

.signup-guide .sg-eyebrow i {
  font-size: 14px;
}

.signup-guide .sg-hero h1 {
  max-width: 570px;
  margin: 0;
  font-size: clamp(38px, 4.2vw, 58px);
  line-height: 1.12;
  font-weight: 800;
  letter-spacing: -2.4px;
}

.signup-guide .sg-hero h1 span {
  color: var(--sg-orange);
}

.signup-guide .sg-hero-description {
  max-width: 490px;
  margin: 22px 0 0;
  color: var(--sg-muted);
  font-size: 16px;
  line-height: 1.85;
}

.signup-guide .sg-hero-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 13px;
  margin-top: 29px;
}

.signup-guide .sg-primary-btn,
.signup-guide .sg-secondary-btn {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  min-height: 48px;
  padding: 0 22px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  transition: transform .2s ease, box-shadow .2s ease, background .2s ease;
}

.signup-guide .sg-primary-btn {
  color: #ffffff;
  background: var(--sg-orange);
  border: 1px solid var(--sg-orange);
  box-shadow: 0 7px 18px rgba(255, 87, 45, .18);
}

.signup-guide .sg-primary-btn:hover {
  background: var(--sg-orange-dark);
  box-shadow: 0 10px 25px rgba(255, 87, 45, .25);
  transform: translateY(-2px);
}

.signup-guide .sg-secondary-btn {
  color: var(--sg-ink);
  background: #ffffff;
  border: 1px solid var(--sg-border);
}

.signup-guide .sg-secondary-btn:hover {
  border-color: #ffc7b7;
  transform: translateY(-2px);
}

.signup-guide .sg-hero-footnote {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-top: 23px;
  color: var(--sg-muted);
  font-size: 12px;
}

.signup-guide .sg-hero-footnote i {
  color: #16a064;
}

/* HERO VISUAL */

.signup-guide .sg-hero-visual {
  position: relative;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, .9);
  border-radius: 25px;
  background: linear-gradient(140deg, #fff1eb, #f2f3ff);
  box-shadow: 0 25px 70px rgba(29, 34, 58, .10);
}

.signup-guide .sg-visual-image {
  display: block;
  width: 100%;
  height: 290px;
  object-fit: contain;
  object-position: center;
  border-radius: 15px;
  background: #ffffff;
}

.signup-guide .sg-floating-card {
  position: absolute;
  right: -22px;
  bottom: -22px;
  display: flex;
  align-items: center;
  gap: 13px;
  max-width: 250px;
  padding: 15px 19px;
  border: 1px solid #ececf2;
  border-radius: 15px;
  background: #ffffff;
  box-shadow: 0 15px 40px rgba(29, 34, 58, .12);
}

.signup-guide .sg-floating-icon {
  display: grid;
  place-items: center;
  flex: 0 0 42px;
  height: 42px;
  color: var(--sg-orange);
  background: #fff0ea;
  border-radius: 12px;
  font-size: 17px;
}

.signup-guide .sg-floating-card strong {
  display: block;
  font-size: 13px;
  line-height: 1.5;
}

.signup-guide .sg-floating-card small {
  display: block;
  margin-top: 3px;
  color: var(--sg-muted);
  font-size: 11px;
}

/* QUICK OVERVIEW */

.signup-guide .sg-overview {
  position: relative;
  z-index: 2;
  margin-top: -29px;
}

.signup-guide .sg-overview-inner {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  padding: 23px 12px;
  border: 1px solid var(--sg-border);
  border-radius: 17px;
  background: #ffffff;
  box-shadow: 0 12px 36px rgba(28, 35, 59, .055);
}

.signup-guide .sg-overview-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 13px;
  padding: 8px 15px;
}

.signup-guide .sg-overview-item + .sg-overview-item {
  border-left: 1px solid var(--sg-border);
}

.signup-guide .sg-overview-item > i {
  display: grid;
  place-items: center;
  flex: 0 0 42px;
  height: 42px;
  border-radius: 12px;
  background: #fff1ec;
  color: var(--sg-orange);
  font-size: 17px;
}

.signup-guide .sg-overview-item strong {
  display: block;
  font-size: 15px;
}

.signup-guide .sg-overview-item span {
  display: block;
  margin-top: 4px;
  color: var(--sg-muted);
  font-size: 12px;
}

/* MAIN CONTENT */

.signup-guide .sg-main {
  padding: 82px 0 90px;
}

.signup-guide .sg-section-heading {
  max-width: 660px;
  margin: 0 auto 48px;
  text-align: center;
}

.signup-guide .sg-section-kicker {
  margin: 0 0 12px;
  color: var(--sg-orange);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.signup-guide .sg-section-heading h2 {
  margin: 0;
  font-size: clamp(28px, 3vw, 38px);
  font-weight: 800;
  letter-spacing: -1.3px;
  line-height: 1.2;
}

.signup-guide .sg-section-heading > p:last-child {
  margin: 14px 0 0;
  color: var(--sg-muted);
  font-size: 15px;
  line-height: 1.8;
}

/* STEPS TIMELINE */

.signup-guide .sg-timeline {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 27px;
}

.signup-guide .sg-timeline::before {
  content: "";
  position: absolute;
  top: 45px;
  bottom: 45px;
  left: 27px;
  width: 2px;
  background: linear-gradient(
    to bottom,
    #ffc6b5,
    #ff8d70 45%,
    #ffc6b5
  );
}

.signup-guide .sg-step {
  position: relative;
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  align-items: start;
  gap: 19px;
}

.signup-guide .sg-step-marker {
  position: relative;
  z-index: 1;
  display: grid;
  place-items: center;
  width: 56px;
  height: 56px;
  border: 5px solid var(--sg-bg);
  border-radius: 50%;
  background: var(--sg-orange);
  color: #ffffff;
  font-size: 13px;
  font-weight: 800;
  box-shadow: 0 0 0 1px #ffd5c9;
}

.signup-guide .sg-step-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(260px, .85fr);
  gap: 30px;
  align-items: center;
  min-width: 0;
  padding: 27px;
  border: 1px solid var(--sg-border);
  border-radius: 19px;
  background: var(--sg-surface);
  box-shadow: 0 7px 25px rgba(29, 34, 58, .035);
  transition: transform .25s ease, border-color .25s ease,
    box-shadow .25s ease;
}

.signup-guide .sg-step-card:hover {
  transform: translateY(-3px);
  border-color: #ffd0c3;
  box-shadow: 0 15px 35px rgba(29, 34, 58, .075);
}

.signup-guide .sg-step-copy {
  min-width: 0;
}

.signup-guide .sg-step-label {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: var(--sg-orange);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.4px;
}

.signup-guide .sg-step-label i {
  font-size: 12px;
}

.signup-guide .sg-step-copy h3 {
  margin: 13px 0 11px;
  font-size: 23px;
  line-height: 1.3;
  font-weight: 800;
  letter-spacing: -.65px;
}

.signup-guide .sg-step-copy > p {
  margin: 0;
  color: var(--sg-muted);
  font-size: 14px;
  line-height: 1.85;
}

.signup-guide .sg-step-tip {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin-top: 20px;
  padding: 12px 13px;
  border-radius: 10px;
  background: #fff7f3;
  color: #8a4a36;
  font-size: 12px;
  line-height: 1.7;
}

.signup-guide .sg-step-tip i {
  margin-top: 3px;
  color: var(--sg-orange);
}

.signup-guide .sg-step-figure {
  position: relative;
  min-width: 0;
  margin: 0;
  padding: 8px;
  border: 1px solid #eeedf3;
  border-radius: 13px;
  background: #f7f8fc;
}

.signup-guide .sg-step-figure img {
  display: block;
  width: 100%;
  height: 210px;
  object-fit: contain;
  object-position: center;
  border-radius: 8px;
  background: #ffffff;
}

.signup-guide .sg-step-figure figcaption {
  padding: 10px 5px 3px;
  color: var(--sg-muted);
  font-size: 11px;
  text-align: center;
}

/* SECURITY NOTE */

.signup-guide .sg-security {
  display: flex;
  align-items: flex-start;
  gap: 17px;
  margin: 48px 0 0 75px;
  padding: 23px 25px;
  border: 1px solid #dcefe5;
  border-radius: 15px;
  background: #f2fbf6;
}

.signup-guide .sg-security-icon {
  display: grid;
  place-items: center;
  flex: 0 0 43px;
  height: 43px;
  border-radius: 13px;
  color: #138452;
  background: #dff5e8;
  font-size: 18px;
}

.signup-guide .sg-security h3 {
  margin: 1px 0 6px;
  font-size: 15px;
  font-weight: 800;
}

.signup-guide .sg-security p {
  margin: 0;
  color: #617d6c;
  font-size: 13px;
  line-height: 1.8;
}

/* FINAL CTA */

.signup-guide .sg-final-cta {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 30px;
  margin-top: 58px;
  padding: 36px 38px;
  border-radius: 20px;
  background: #191c2c;
  color: #ffffff;
}

.signup-guide .sg-final-cta::after {
  content: "";
  position: absolute;
  top: -100px;
  right: -30px;
  width: 260px;
  height: 260px;
  border: 1px solid rgba(255, 255, 255, .09);
  border-radius: 50%;
  box-shadow: 0 0 0 35px rgba(255,255,255,.025),
    0 0 0 70px rgba(255,255,255,.02);
  z-index: -1;
}

.signup-guide .sg-final-copy {
  max-width: 600px;
}

.signup-guide .sg-final-copy h2 {
  margin: 0;
  font-size: clamp(23px, 2.5vw, 30px);
  line-height: 1.35;
  letter-spacing: -.8px;
}

.signup-guide .sg-final-copy h2 span {
  color: #ff886c;
}

.signup-guide .sg-final-copy p {
  margin: 10px 0 0;
  color: #aeb4c9;
  font-size: 13px;
  line-height: 1.8;
}

.signup-guide .sg-final-cta .sg-primary-btn {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  white-space: nowrap;
}

/* FOOTER */

.signup-guide .sg-footer {
  padding: 25px 0;
  border-top: 1px solid var(--sg-border);
  background: #ffffff;
}

.signup-guide .sg-footer-inner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 18px;
}

.signup-guide .sg-footer p {
  margin: 0;
  color: var(--sg-muted);
  font-size: 12px;
  line-height: 1.7;
}

.signup-guide .sg-footer a {
  color: var(--sg-orange-dark);
  font-weight: 700;
}

.signup-guide .sg-footer a:hover {
  text-decoration: underline;
}

/* ACCESSIBILITY */

.signup-guide a:focus-visible {
  outline: 3px solid rgba(255, 87, 45, .35);
  outline-offset: 4px;
}

/* RESPONSIVE */

@media (max-width: 900px) {
  .signup-guide .sg-hero-grid {
    grid-template-columns: 1fr .9fr;
    gap: 30px;
  }

  .signup-guide .sg-visual-image {
    height: 240px;
  }

  .signup-guide .sg-step-card {
    grid-template-columns: 1fr;
    gap: 21px;
  }

  .signup-guide .sg-step-figure img {
    height: 260px;
  }

  .signup-guide .sg-floating-card {
    right: -8px;
  }
}

@media (max-width: 650px) {
  .signup-guide .sg-container {
    width: calc(100% - 32px);
  }

  .signup-guide .sg-hero {
    padding: 48px 0 62px;
  }

  .signup-guide .sg-hero-grid {
    grid-template-columns: 1fr;
    gap: 35px;
  }

  .signup-guide .sg-hero h1 {
    font-size: 40px;
    letter-spacing: -1.6px;
  }

  .signup-guide .sg-hero-description {
    font-size: 14px;
  }

  .signup-guide .sg-hero-visual {
    margin-right: 8px;
    padding: 10px;
  }

  .signup-guide .sg-visual-image {
    height: 220px;
  }

  .signup-guide .sg-floating-card {
    right: -8px;
    bottom: -20px;
    max-width: 220px;
    padding: 11px 13px;
  }

  .signup-guide .sg-overview {
    margin-top: -22px;
  }

  .signup-guide .sg-overview-inner {
    grid-template-columns: 1fr;
    padding: 10px 17px;
  }

  .signup-guide .sg-overview-item {
    justify-content: flex-start;
    padding: 13px 5px;
  }

  .signup-guide .sg-overview-item + .sg-overview-item {
    border-top: 1px solid var(--sg-border);
    border-left: 0;
  }

  .signup-guide .sg-main {
    padding: 62px 0;
  }

  .signup-guide .sg-section-heading {
    margin-bottom: 35px;
  }

  .signup-guide .sg-section-heading h2 {
    font-size: 30px;
  }

  .signup-guide .sg-section-heading > p:last-child {
    font-size: 13px;
  }

  .signup-guide .sg-timeline {
    gap: 20px;
  }

  .signup-guide .sg-timeline::before {
    left: 21px;
  }

  .signup-guide .sg-step {
    grid-template-columns: 44px minmax(0, 1fr);
    gap: 12px;
  }

  .signup-guide .sg-step-marker {
    width: 44px;
    height: 44px;
    border-width: 4px;
    font-size: 11px;
  }

  .signup-guide .sg-step-card {
    padding: 19px;
    border-radius: 15px;
  }

  .signup-guide .sg-step-copy h3 {
    font-size: 20px;
  }

  .signup-guide .sg-step-copy > p {
    font-size: 13px;
  }

  .signup-guide .sg-step-figure img {
    height: 190px;
  }

  .signup-guide .sg-security {
    margin: 30px 0 0 0;
    padding: 18px;
    gap: 12px;
  }

  .signup-guide .sg-final-cta {
    align-items: flex-start;
    flex-direction: column;
    padding: 28px 23px;
    margin-top: 40px;
  }

  .signup-guide .sg-final-cta .sg-primary-btn {
    width: 100%;
  }

  .signup-guide .sg-footer-inner {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (prefers-reduced-motion: reduce) {
  .signup-guide *,
  .signup-guide *::before,
  .signup-guide *::after {
    scroll-behavior: auto !important;
    transition-duration: .01ms !important;
    animation-duration: .01ms !important;
  }
}
`;

function HowToSignUp({
    helpUrl = "/help",
    homeUrl = "/",
    loginUrl = "/login",
    signupUrl = "/signup",
}) {
    return (
        <main className="signup-guide">
            <style>{styles}</style>

            {/* Hero */}
            {/* <section className="sg-hero">
                <div className="sg-container sg-hero-grid">
                    <div className="sg-hero-copy">
                        <div className="sg-eyebrow">
                            <i className="fas fa-sparkles" aria-hidden="true" />
                            YOUR EVENTFORK JOURNEY
                        </div>

                        <h1>
                            Your next great experience starts <span>here.</span>
                        </h1>

                        <p className="sg-hero-description">
                            Create your EventFork account in just a few simple steps.
                            Discover exciting events, connect with people, and never miss
                            an experience worth remembering.
                        </p>

                        <div className="sg-hero-actions">
                            <a className="sg-primary-btn" href={signupUrl}>
                                Create Your Account
                                <i className="fas fa-arrow-right" aria-hidden="true" />
                            </a>

                            <a className="sg-secondary-btn" href={loginUrl}>
                                <i className="fas fa-right-to-bracket" aria-hidden="true" />
                                Already a member?
                            </a>
                        </div>

                        <div className="sg-hero-footnote">
                            <i className="fas fa-shield-halved" aria-hidden="true" />
                            Simple setup · Email verification · Secure access
                        </div>
                    </div>

                    <div className="sg-hero-visual">
                        <img
                            className="sg-visual-image"
                            src={signupImage}
                            alt="Get started with EventFork"
                        />

                        <div className="sg-floating-card">
                            <div className="sg-floating-icon">
                                <i className="fas fa-check-circle" aria-hidden="true" />
                            </div>
                            <div>
                                <strong>Your journey starts here</strong>
                                <small>Four simple steps to get started</small>
                            </div>
                        </div>
                    </div>
                </div>
            </section> */}

            {/* Quick overview */}
            <section className="sg-overview" aria-label="Sign-up overview">
                <div className="sg-container sg-overview-inner">
                    <div className="sg-overview-item">
                        <i className="fas fa-list-check" aria-hidden="true" />
                        <div>
                            <strong>4 Simple Steps</strong>
                            <span>Easy-to-follow instructions</span>
                        </div>
                    </div>

                    <div className="sg-overview-item">
                        <i className="fas fa-clock" aria-hidden="true" />
                        <div>
                            <strong>Just a Few Minutes</strong>
                            <span>Quick account setup</span>
                        </div>
                    </div>

                    <div className="sg-overview-item">
                        <i className="fas fa-user-shield" aria-hidden="true" />
                        <div>
                            <strong>Secure Account</strong>
                            <span>Verify your email to continue</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Step-by-step guide */}
            <section className="sg-main">
                <div className="sg-container">
                    <div className="sg-section-heading">
                        <p className="sg-section-kicker">THE PROCESS</p>
                        <h2>How to Sign Up on EventFork</h2>
                        <p>
                            Follow these four steps to create your account and start
                            exploring events. Each step includes a visual guide to help
                            you along the way.
                        </p>
                    </div>

                    <div className="sg-timeline">
                        {steps.map((step) => (
                            <article className="sg-step" key={step.number}>
                                <div className="sg-step-marker" aria-hidden="true">
                                    {step.number}
                                </div>

                                <div className="sg-step-card">
                                    <div className="sg-step-copy">
                                        <span className="sg-step-label">
                                            <i className={`fas ${step.icon}`} aria-hidden="true" />
                                            {step.label}
                                        </span>

                                        <h3>{step.title}</h3>

                                        <p>{step.description}</p>

                                        <div className="sg-step-tip">
                                            <i className="fas fa-lightbulb" aria-hidden="true" />
                                            <span>{step.tip}</span>
                                        </div>
                                    </div>

                                    <figure className="sg-step-figure">
                                        <img
                                            src={step.image}
                                            alt={step.alt}
                                            loading="lazy"
                                        />
                                        <figcaption>
                                            Step {step.number} — {step.title}
                                        </figcaption>
                                    </figure>
                                </div>
                            </article>
                        ))}
                    </div>

                    {/* Security information */}
                    <aside className="sg-security">
                        <div className="sg-security-icon">
                            <i className="fas fa-lock" aria-hidden="true" />
                        </div>
                        <div>
                            <h3>Keep your account secure</h3>
                            <p>
                                Use a strong password, verify your email through the
                                official verification message, and never share your
                                password with anyone.
                            </p>
                        </div>
                    </aside>

                    {/* Final call to action */}
                    <section className="sg-final-cta">
                        <div className="sg-final-copy">
                            <h2>
                                Ready to discover something <span>extraordinary?</span>
                            </h2>
                            <p>
                                Create your account today and start exploring events
                                that match your interests.
                            </p>
                        </div>

                        <a className="sg-primary-btn" href={signupUrl}>
                            Sign Up Now
                            <i className="fas fa-arrow-right" aria-hidden="true" />
                        </a>
                    </section>
                </div>
            </section>

            {/* Footer */}
            <footer className="sg-footer">
                <div className="sg-container sg-footer-inner">
                    <p>
                        © {new Date().getFullYear()} EventFork. All rights reserved.
                    </p>

                    <p>
                        Need assistance?{" "}
                        <a href={helpUrl}>Visit our Help Center</a>
                        {" · "}
                        <a href={homeUrl}>Back to Home</a>
                    </p>
                </div>
            </footer>
        </main>
    );
}

export default HowToSignUp;
